// Tasks page for viewing chatbot task groups

import { useParams, useSearchParams } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { LoadingSpinner } from '@/client/components/shared/LoadingSpinner';
import { TasksTable } from '@/client/components/chatbots/TasksTable';
import { DateRangeFilter } from '@/client/components/shared/DateRangeFilter';
import { useDataGroups, useChatbot } from '@/lib/sdk/chatbot/queries';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { helpers } from '@/client/utils/helpers';
import { generateFormDynamicColumns, generateFormStaticColumns } from '@/lib/sdk/chatbot/types';
import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';

export function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { botId, orgId } = useParams<{ botId: string; orgId: string }>();
  const { user, userPermission } = useAuth();

  const getDefaultDateRange = useCallback(() => {
    const endDate = dayjs().add(1, 'day');
    const startDate = endDate.subtract(3, 'days');
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  }, []);

  const dateRange = useMemo(() => {
    const defaults = getDefaultDateRange();
    return {
      startDate: searchParams.get('start_date') || defaults.startDate,
      endDate: searchParams.get('end_date') || defaults.endDate,
    };
  }, [searchParams, getDefaultDateRange]);

  const { data: taskGroupsData, isLoading: tasksLoading } = useDataGroups(botId!, {
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
  });
  const { data: chatbot, isLoading: chatbotsLoading } = useChatbot(botId!);
  const taskGroups = taskGroupsData?.items || [];

  const handleDateRangeChange = useCallback((startDate: string, endDate: string) => {
    setSearchParams({ start_date: startDate, end_date: endDate });
  }, [setSearchParams]);

  const handleReset = useCallback(() => {
    const defaults = getDefaultDateRange();
    setSearchParams({ start_date: defaults.startDate, end_date: defaults.endDate });
  }, [getDefaultDateRange, setSearchParams]);

  const canEdit = helpers.hasPermission(userPermission || 'read', 'read_write') || user?.is_super_admin === true;
  const isLoading = tasksLoading || chatbotsLoading;

  const breadcrumbs = [
    { label: 'Workflows', href: `/dashboard/${orgId}/workflows` },
    { label: 'Data' },
  ];

  const columns = [
    ...generateFormStaticColumns(),
    ...(chatbot?.task_list ? generateFormDynamicColumns(chatbot?.task_list) : []),
  ];

  if (isLoading) {
    return <LoadingSpinner message="Loading tasks..." />;
  }

  return (
    <PageContainer title="Tasks" breadcrumbs={breadcrumbs}>
      <Box mb={6}>
        <DateRangeFilter
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onDateRangeChange={handleDateRangeChange}
          onReset={handleReset}
        />
      </Box>
      <TasksTable
        dynamicColumns={columns}
        taskGroups={taskGroups}
        canEdit={canEdit}
      />
    </PageContainer>
  );
}
